import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import TrashIcon from "./TrashIcon";
import "./CheckinForm.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const METER_FIELDS = [
  "reading_gas",
  "reading_electricity",
  "reading_electricity_low",
  "reading_electricity_high",
  "reading_water",
  "reading_heat_pump",
];

const TENANT_FIELDS = ["tenant_1", "tenant_2", "tenant_3"];

// Standard names, so the same room reads the same across every report — in the
// order an inspector walks the place. Anything else is typed by hand.
const ROOM_PRESETS = [
  "Entrance",
  "Living Room",
  "Dining Room",
  "Kitchen",
  "Bedroom 1",
  "Bedroom 2",
  "Bedroom 3",
  "Bedroom 4",
  "Bathroom",
  "Toilet",
  "Home Office",
  "Utility room",
  "Storage Room",
  "Garage",
  "Basement",
  "Loft",
  "Stairs",
  "Balcony",
  "Garden",
];

// "tentant" is the backend's spelling.
const tenantSignatureField = (tenantField) =>
  `signature_${tenantField.replace("tenant", "tentant")}_blob`;

const formatTime = (date) =>
  date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });

const startOfDay = (date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
};

// Whole days between two midnights, so DST cannot shift the count.
const daysAgo = (date) =>
  Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);

// The time alone is enough for a save you just made; anything older needs a
// day to hang it on, and a year once it is not from this one.
const formatSavedAt = (date) => {
  const time = formatTime(date);
  const days = daysAgo(date);

  if (days <= 0) return time;
  if (days === 1) return `yesterday ${time}`;
  if (days < 7)
    return `${date.toLocaleDateString("en-GB", { weekday: "short" })} ${time}`;

  const day = date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === new Date().getFullYear()
      ? {}
      : { year: "numeric" }),
  });
  return `${day}, ${time}`;
};

const RATING_LABEL = {
  good: "Good",
  insufficient: "Insufficient",
  poor: "Poor",
};

const filledCount = (checkin, fields) =>
  fields.filter((field) => {
    const value = checkin?.[field];
    return value !== null && value !== undefined && value !== "";
  }).length;

// A photo list from the API, counted — older payloads send the number itself.
const photoCount = (photos) =>
  Array.isArray(photos) ? photos.length : photos ?? 0;

const normaliseRoom = (room, index) => ({
  id: room.id ?? `r${index + 1}`,
  name: room.name ?? `Room ${index + 1}`,
  // The API calls it condition; the list renders it as good/insufficient/poor.
  rating: room.condition ?? null,
  photos: photoCount(room.photos),
  hasDamage: Boolean(room.has_damage),
  damageComments: room.damage_comments ?? "",
  // Rooms without an order from the API keep their payload position.
  order: Number.isFinite(room.order) ? room.order : index + 1,
});

const byOrder = (a, b) => a.order - b.order;

const CheckinForm = () => {
  const { cfid } = useParams();
  const navigate = useNavigate();

  const { sendRequest: getCheckinRequest, isLoading, error } = useHttpNoAuth();
  const {
    sendRequest: addRoomRequest,
    isLoading: isAddingRoom,
    error: addRoomError,
  } = useHttpNoAuth();
  const {
    sendRequest: deleteRoomRequest,
    isLoading: isDeletingRoom,
    error: deleteRoomError,
  } = useHttpNoAuth();
  const {
    sendRequest: distributeRequest,
    isLoading: isDistributing,
    error: distributeError,
  } = useHttpNoAuth();

  const [checkin, setCheckin] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [addingRoom, setAddingRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  // A preset is picked; a custom name is typed. Only one of the two is in play.
  const [isCustomRoom, setIsCustomRoom] = useState(false);
  const [keysComment, setKeysComment] = useState("");
  const [addErrorVisible, setAddErrorVisible] = useState(false);
  const [confirmingRoomId, setConfirmingRoomId] = useState(null);
  const [deletingRoomId, setDeletingRoomId] = useState(null);
  const [reportUrl, setReportUrl] = useState(null);

  const getCheckin = useCallback(() => {
    if (!cfid) return;
    getCheckinRequest(
      { url: `${BACKEND_URL}/welocate/checkins/${cfid}`, method: "GET" },
      (data) => {
        setCheckin(data);
        setKeysComment(data?.keys_comments ?? "");
        const fetchedRooms = (data?.rooms ?? [])
          .map(normaliseRoom)
          .sort(byOrder);
        setRooms(fetchedRooms);
      }
    );
  }, [cfid, getCheckinRequest]);

  useEffect(() => {
    getCheckin();
  }, [getCheckin]);

  // The same form runs both handovers — every label follows the type the API
  // sends, and anything that is not a check-out is a check-in.
  const isCheckout = checkin?.type === "checkout";
  const typeLabel = isCheckout ? "Check-out" : "Check-in";

  // A check-in that has never been touched has no last_saved_on.
  const savedAt = useMemo(() => {
    if (!checkin?.last_saved_on) return null;
    const date = new Date(checkin.last_saved_on);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [checkin]);

  const address = useMemo(() => {
    if (!checkin) return "";
    return [checkin.street, checkin.number, checkin.floor]
      .filter(Boolean)
      .join(" ");
  }, [checkin]);

  // Only the tenants actually on this check-in — there can be one, two or three.
  const presentTenantFields = useMemo(
    () => TENANT_FIELDS.filter((field) => Boolean(checkin?.[field])),
    [checkin]
  );

  const tenants = useMemo(() => {
    const names = presentTenantFields.map((field) => checkin[field]);
    if (names.length < 2) return names.join("");
    return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
  }, [checkin, presentTenantFields]);

  const meters = useMemo(
    () => ({
      filled: filledCount(checkin, METER_FIELDS),
      total: METER_FIELDS.length,
    }),
    [checkin]
  );

  // Keys and meters keep their photos on the check-in itself, beside the rooms'.
  const handoverPhotos = useMemo(
    () => ({
      keys: photoCount(checkin?.keys_photos),
      meters: photoCount(checkin?.meter_photos),
    }),
    [checkin]
  );

  // Welocate signs, plus one signature per tenant present.
  const signatures = useMemo(() => {
    const fields = [
      "welocate_signature_blob",
      ...presentTenantFields.map(tenantSignatureField),
    ];
    return {
      signed: filledCount(checkin, fields),
      total: fields.length,
    };
  }, [checkin, presentTenantFields]);

  // Absent on older check-ins, which have not been signed off either.
  const isSignedOff = Boolean(checkin?.is_signed_off);

  const ratedCount = useMemo(
    // Same test the row makes, so a blank condition is "Not started" in both.
    () => rooms.filter((room) => Boolean(room.rating)).length,
    [rooms]
  );

  // The report rates every room, so an unrated one — or no rooms at all —
  // is nothing to sign off on yet.
  const unratedCount = rooms.length - ratedCount;
  const canSignOff = rooms.length > 0 && unratedCount === 0;

  // Every rating, meter reading and signature counts equally towards done.
  const progress = useMemo(() => {
    const total = rooms.length + meters.total + signatures.total;
    if (total === 0) return 0;
    const done = ratedCount + meters.filled + signatures.signed;
    return Math.round((done / total) * 100);
  }, [rooms.length, ratedCount, meters, signatures]);

  const deleteRoom = (id) => {
    if (isDeletingRoom || isSignedOff) return;
    setDeletingRoomId(id);
    deleteRoomRequest(
      { url: `${BACKEND_URL}/welocate/rooms/${id}`, method: "DELETE" },
      () => {
        setDeletingRoomId(null);
        setConfirmingRoomId(null);
        getCheckin();
      }
    );
  };

  // The hook never rejects, so a failed delete has to release the row here.
  useEffect(() => {
    if (deleteRoomError) setDeletingRoomId(null);
  }, [deleteRoomError]);

  const submitNewRoom = (event) => {
    event.preventDefault();
    const name = newRoomName.trim();
    if (!name || isAddingRoom || isSignedOff) return;
    setAddErrorVisible(true);
    addRoomRequest(
      {
        url: `${BACKEND_URL}/welocate/checkins/${cfid}/rooms`,
        method: "POST",
        body: { name },
      },
      () => {
        // The new room — id and order included — comes back with the refetch.
        setAddErrorVisible(false);
        setNewRoomName("");
        setIsCustomRoom(false);
        setAddingRoom(false);
        getCheckin();
      }
    );
  };

  // Tapping the picked name again clears it, so nothing is ever stuck selected.
  const selectPreset = (name) => {
    setIsCustomRoom(false);
    setNewRoomName((current) => (current === name ? "" : name));
  };

  const startCustomRoom = () => {
    setIsCustomRoom(true);
    setNewRoomName("");
  };

  const startAddingRoom = () => {
    if (isSignedOff) return;
    setAddErrorVisible(false);
    setNewRoomName("");
    setIsCustomRoom(false);
    setAddingRoom(true);
  };

  const cancelNewRoom = useCallback(() => {
    setAddErrorVisible(false);
    setNewRoomName("");
    setIsCustomRoom(false);
    setAddingRoom(false);
  }, []);

  // Escape closes the sheet, and the page behind it stays put while it is open.
  useEffect(() => {
    if (!addingRoom) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !isAddingRoom) cancelNewRoom();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [addingRoom, isAddingRoom, cancelNewRoom]);

  // Rooms already on the check-in stay pickable — two bathrooms are a real
  // thing — but the list says so, so nobody adds one twice by accident.
  const usedRoomNames = useMemo(
    () => new Set(rooms.map((room) => room.name.trim().toLowerCase())),
    [rooms]
  );

  const openRoom = (id) => {
    navigate(`/checkin/${cfid}/rooms/${id}`);
  };

  // Distribution happens on the backend; the response is where the report
  // now lives, so the confirmation can link straight to it.
  const distributeReport = () => {
    if (isDistributing) return;
    setReportUrl(null);
    distributeRequest(
      { url: `${BACKEND_URL}/welocate/checkins/${cfid}/report`, method: "GET" },
      (data) => {
        setReportUrl(typeof data === "string" ? data : data?.url ?? null);
      }
    );
  };

  return (
    <div className="cf-page">
      <header className="cf-topbar">
        {/* This page is the top of the flow — nothing to go back to. */}
        <span className="cf-back cf-back--static" aria-hidden="true">
          <HomeOutlinedIcon />
        </span>

        <div className="cf-topbar-titles">
          <h1 className="cf-topbar-title">{address || typeLabel}</h1>
          <p className="cf-topbar-sub">
            {checkin
              ? `${checkin.postal_code ?? ""} ${
                  checkin.city ?? ""
                } · ${typeLabel}`.trim()
              : typeLabel}
          </p>
        </div>

        {savedAt && (
          <span
            className="cf-saved"
            role="status"
            // The badge is abbreviated — the exact moment lives in the tooltip.
            title={savedAt.toLocaleString("nl-NL")}
          >
            <CheckCircleOutlineIcon />
            Saved{" "}
            <span className="cf-saved-time">{formatSavedAt(savedAt)}</span>
          </span>
        )}
      </header>

      {/* The type only arrives with the record, so this one cannot name it. */}
      {isLoading && !checkin && <p className="cf-state">Loading…</p>}

      {!isLoading && error && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">{error}</p>
          <button type="button" className="cf-state-retry" onClick={getCheckin}>
            Try again
          </button>
        </div>
      )}

      {checkin && (
        <main className="cf-main cf-main--flow">
          {isSignedOff && (
            <section className="cf-signedoff" role="status">
              <span className="cf-signedoff-icon">
                <CheckIcon />
              </span>
              <span className="cf-signedoff-body">
                <span className="cf-signedoff-title">Signed off</span>
                <span className="cf-signedoff-sub">
                  Completed · {signatures.signed} signature
                  {signatures.signed === 1 ? "" : "s"} · nothing can be changed
                </span>
              </span>
            </section>
          )}

          <section className="cf-card cf-summary">
            {/* Postal code and city read as one place, the way the top bar
                writes them. A record missing either keeps the address on its
                own, rather than trailing a separator with nothing after it. */}
            <h2 className="cf-summary-title">
              {[
                address,
                [checkin.postal_code, checkin.city].filter(Boolean).join(" "),
              ]
                .filter(Boolean)
                .join(" · ")}
            </h2>
            <p className="cf-summary-tenants">{tenants}</p>

            <div
              className="cf-progress"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Inspection progress"
            >
              <div className="cf-progress-bar" style={{ width: `${progress}%` }} />
            </div>

            <div className="cf-stats">
              <div className="cf-stat">
                <div className="cf-stat-value">
                  {ratedCount}/{rooms.length}
                </div>
                <div className="cf-stat-label">RATED</div>
              </div>
              <div className="cf-stat">
                <div className="cf-stat-value">
                  {meters.filled}/{meters.total}
                </div>
                <div className="cf-stat-label">METERS</div>
              </div>
              <div className="cf-stat">
                <div className="cf-stat-value">
                  {signatures.signed}/{signatures.total}
                </div>
                <div className="cf-stat-label">SIGNATURES</div>
              </div>
            </div>
          </section>

          <div className="cf-section-head">
            <h3 className="cf-section-title">ROOMS</h3>
            <span className="cf-section-hint">
              {isSignedOff ? "Tap to view" : "Tap to fill in"}
            </span>
          </div>

          <section className="cf-card">
            {rooms.length === 0 && (
              <p className="cf-empty">
                {isSignedOff
                  ? "No rooms were inspected."
                  : "No rooms yet — add the first one below."}
              </p>
            )}

            {rooms.map((room, index) =>
              // A room takes its photos and notes down with it, so the trash
              // asks first and the row itself becomes the question.
              confirmingRoomId === room.id ? (
                <div className="cf-room" key={room.id}>
                  <div className="cf-room-confirm" role="alertdialog">
                    <span className="cf-room-confirm-text">
                      Delete <strong>{room.name}</strong>? This cannot be undone.
                    </span>
                    <div className="cf-room-confirm-actions">
                      <button
                        type="button"
                        className="cf-room-confirm-yes"
                        disabled={isDeletingRoom}
                        onClick={() => deleteRoom(room.id)}
                      >
                        {deletingRoomId === room.id ? "Deleting…" : "Delete"}
                      </button>
                      <button
                        type="button"
                        className="cf-room-confirm-no"
                        disabled={isDeletingRoom}
                        onClick={() => setConfirmingRoomId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="cf-room" key={room.id}>
                  <button
                    type="button"
                    className="cf-room-open"
                    onClick={() => openRoom(room.id)}
                  >
                    <span
                      className={`cf-room-index${
                        room.rating ? ` cf-room-index--${room.rating}` : ""
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span className="cf-room-body">
                      <span className="cf-room-name">{room.name}</span>
                      <span className="cf-room-meta">
                        <ImageOutlinedIcon />
                        <span className="cf-room-photos">{room.photos}</span>
                        {room.rating ? RATING_LABEL[room.rating] : "Not started"}
                        {room.hasDamage && (
                          // The comment is the only place the damage is described,
                          // and the row has no space for it.
                          <span
                            className="cf-room-damage"
                            title={room.damageComments || undefined}
                          >
                            <WarningAmberOutlinedIcon />
                            Damage
                          </span>
                        )}
                      </span>
                    </span>

                    <ChevronRightIcon className="cf-room-chevron" />
                  </button>

                  {/* A signed-off room is part of the record, so it cannot be
                      taken out of the report any more. */}
                  {!isSignedOff && (
                    <button
                      type="button"
                      className="cf-room-delete"
                      aria-label={`Delete ${room.name}`}
                      disabled={isDeletingRoom || isLoading}
                      onClick={() => setConfirmingRoomId(room.id)}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              )
            )}
          </section>

          {!isDeletingRoom && deleteRoomError && (
            <p className="cf-add-error" role="alert">
              {deleteRoomError}
            </p>
          )}

          {!isSignedOff && (
            <button type="button" className="cf-add" onClick={startAddingRoom}>
              <AddIcon />
              Add a room
            </button>
          )}

          <div className="cf-section-head">
            <h3 className="cf-section-title">HANDOVER</h3>
          </div>

          <section className="cf-card">
            <button
              type="button"
              className="cf-handover-row"
              onClick={() => navigate(`/checkin/${cfid}/meters`)}
            >
              <span className="cf-handover-icon">
                <SpeedOutlinedIcon />
              </span>
              <span className="cf-handover-body">
                <span className="cf-handover-title">Meter readings</span>
                <span className="cf-handover-sub">
                  <span
                    className="cf-handover-photos"
                    aria-label={`${handoverPhotos.meters} photo${
                      handoverPhotos.meters === 1 ? "" : "s"
                    }`}
                  >
                    <ImageOutlinedIcon />
                    {handoverPhotos.meters}
                  </span>
                  {meters.filled} of {meters.total} filled in
                </span>
              </span>
              <ChevronRightIcon className="cf-room-chevron" />
            </button>

            <button
              type="button"
              className="cf-handover-row"
              onClick={() => navigate(`/checkin/${cfid}/keys`)}
            >
              <span className="cf-handover-icon">
                <VpnKeyOutlinedIcon />
              </span>
              <span className="cf-handover-body">
                <span className="cf-handover-title">Keys</span>
                <span className="cf-handover-sub">
                  <span
                    className="cf-handover-photos"
                    aria-label={`${handoverPhotos.keys} photo${
                      handoverPhotos.keys === 1 ? "" : "s"
                    }`}
                  >
                    <ImageOutlinedIcon />
                    {handoverPhotos.keys}
                  </span>
                  {keysComment.trim() ? "Comment added" : "Comment required"}
                </span>
              </span>
              <ChevronRightIcon className="cf-room-chevron" />
            </button>
          </section>

          {/* <p className="cf-note">
            Rooms are rated <strong>Good</strong>, <strong>Insufficient</strong>{" "}
            or <strong>Poor</strong> — “sufficient” read as a pass and a fail at
            once, so it is gone.
          </p> */}

          <div className="cf-page-actions">
            <button
              type="button"
              className="cf-btn cf-btn--primary"
              disabled={!isSignedOff && !canSignOff}
              onClick={() => navigate(`/checkin/${cfid}/signoff`)}
            >
              {isSignedOff ? "View signoff details" : "Sign off"}
            </button>

            {!isSignedOff && !canSignOff && (
              <p className="cf-actions-hint">
                {rooms.length === 0
                  ? "Add at least one room before signing off."
                  : `Rate ${unratedCount} more room${
                      unratedCount === 1 ? "" : "s"
                    } before signing off.`}
              </p>
            )}
            {/* The PDF is the signed record, so it only exists once the
                report has been signed off. */}
            <button
              type="button"
              className="cf-btn cf-btn--ghost"
              disabled={!isSignedOff || isDistributing}
              onClick={distributeReport}
            >
              <SendOutlinedIcon />
              {isDistributing ? "Distributing PDF…" : "Distribute PDF"}
            </button>

            {!isDistributing && distributeError && (
              <p className="cf-add-error" role="alert">
                {distributeError}
              </p>
            )}

            {reportUrl && (
              <div className="cf-distributed" role="status">
                <span className="cf-distributed-icon">
                  <CheckCircleOutlineIcon />
                </span>
                <span className="cf-distributed-body">
                  <span className="cf-distributed-title">
                    Report distributed
                  </span>
                  <a
                    className="cf-distributed-link"
                    href={reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View the report
                    <OpenInNewOutlinedIcon />
                  </a>
                </span>
              </div>
            )}
          </div>
        </main>
      )}

      {addingRoom && (
        <div
          className="cf-sheet-overlay"
          // A tap outside is a cancel, but not while the room is being saved.
          onClick={() => !isAddingRoom && cancelNewRoom()}
        >
          <form
            className="cf-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cf-sheet-title"
            onClick={(event) => event.stopPropagation()}
            onSubmit={submitNewRoom}
          >
            <span className="cf-sheet-grabber" aria-hidden="true" />

            <h2 className="cf-sheet-title" id="cf-sheet-title">
              Add a room
            </h2>
            <p className="cf-sheet-sub">
              Pick a standard name so the report reads the same everywhere — or
              type your own.
            </p>

            <div className="cf-sheet-options">
              {ROOM_PRESETS.map((preset) => {
                const isSelected = !isCustomRoom && newRoomName === preset;
                return (
                  <button
                    type="button"
                    key={preset}
                    className={`cf-sheet-option${
                      isSelected ? " cf-sheet-option--selected" : ""
                    }`}
                    aria-pressed={isSelected}
                    disabled={isAddingRoom}
                    onClick={() => selectPreset(preset)}
                  >
                    <span className="cf-sheet-option-name">{preset}</span>
                    {usedRoomNames.has(preset.toLowerCase()) && (
                      <span className="cf-sheet-option-used">Added</span>
                    )}
                  </button>
                );
              })}

              {isCustomRoom ? (
                <input
                  className="cf-sheet-custom-input"
                  type="text"
                  autoFocus
                  placeholder="Room name"
                  value={newRoomName}
                  disabled={isAddingRoom}
                  onChange={(event) => setNewRoomName(event.target.value)}
                />
              ) : (
                <button
                  type="button"
                  className="cf-sheet-option cf-sheet-option--custom"
                  disabled={isAddingRoom}
                  onClick={startCustomRoom}
                >
                  <EditOutlinedIcon />
                  Type a custom name…
                </button>
              )}
            </div>

            {addErrorVisible && !isAddingRoom && addRoomError && (
              <p className="cf-add-error" role="alert">
                {addRoomError}
              </p>
            )}

            <div className="cf-sheet-actions">
              <button
                type="button"
                className="cf-btn cf-btn--ghost"
                disabled={isAddingRoom}
                onClick={cancelNewRoom}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="cf-btn cf-btn--primary"
                disabled={isAddingRoom || !newRoomName.trim()}
              >
                {isAddingRoom ? "Adding…" : "Add room"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CheckinForm;
